// Must be the FIRST import: dependency-free fatal-error beacon that captures
// the JS message/stack of release boot crashes (which otherwise die as a bare
// native SIGABRT with no Sentry event and no readable TestFlight log) and
// POSTs it to the public /api/error/log endpoint before the app aborts.
import "./app/helpers/bootTrace";
// Second import: installs Promise.allSettled / Promise.any polyfills
// before any module (App, navigators, screens) can call them on Hermes, where
// React Native's bundled Promise lacks both. See promisePolyfills.js for details.
import "./app/helpers/promisePolyfills";
import React from "react";
import {getUserInfo} from "./app/helpers/storage";
import * as Sentry from "@sentry/react-native";
import {LogBox, Platform} from 'react-native';
import {enableFreeze, enableScreens} from 'react-native-screens';
import Observables from '@happy-gastro/global-observables';
import {setPushBackgroundMessageHandler} from './app/services/pushNotifications';
import {presentPushAsLocalNotification} from './app/helpers/mailNotifications';
import {registerRootComponent} from 'expo';
import {
    setupCallKeep,
    displayIncomingCallFromPush,
    endIncomingCallFromPush,
    registerCallKeepEvents,
    bringAppToForeground,
    startIncomingCallNotificationRouting,
} from './app/services/callKeepClient';
import { recordPendingAnswer, clearPendingAnswer } from './app/services/voipPendingAnswer';
import {setupVoipPushKit, isVoipPushKitActive} from './app/services/voipPushKit';
import {navigateWhenReady} from './app/helpers/navigationRef';
// Registers the Driver GPS background task (TaskManager.defineTask) at module
// scope so it exists on a headless background launch, before React mounts.
import './app/helpers/driverTracking';
import App from './App';
import {hydratePosPreferences} from './app/helpers/posPreferences';
import {installModalOrientationDefault} from './app/helpers/modalOrientations';
import {maybeOpenNativeApp} from './app/helpers/deepLink/webOpenInApp';

// Timestamp at JS bundle evaluation, used to measure how long the JS engine
// takes from booting this entry module to mounting the first React tree — a
// real cold-start performance signal on low-end Android POS hardware. See the
// `app.boot` / `app.js_init_to_mount` metrics emitted in Root below.
const jsInitAt = Date.now();

// Load the persisted POS layout / haptics preferences once, ahead of the first screen.
hydratePosPreferences();

/**
 * Emit a Sentry metric without ever letting telemetry break the app. Metrics
 * are auto-enabled by Sentry.init() (SDK >= 7.8.0) but stay invisible in the
 * dashboard until something actually emits — these are the first emissions.
 * @param {'count'|'gauge'|'distribution'} kind Metric type.
 * @param {string} name Metric key.
 * @param {number} value Value to record.
 * @param {object} [options] Optional Sentry metric options (unit/attributes).
 * @returns {void}
 */
const trackMetric = (kind, name, value, options) => {
    try {
        Sentry.metrics?.[kind]?.(name, value, options);
    } catch (_) {
        // Never fail boot because of telemetry.
    }
};

// Enable native screen containers for better performance
if (Platform.OS !== 'web') {
    enableFreeze(true)
    enableScreens();
}
// iOS: React Native only lets a <Modal> rotate on iPads; on iPhone-idiom devices (landscape
// phones, a foldable iPhone's wide inner screen) every dialog would snap to portrait.
// One class-level default fixes all ~150 modals. Guarded: it must never block boot.
try {
    installModalOrientationDefault();
} catch (_) {
    // Cosmetic only.
}
// ll LogBox warnings and errors (yellow and red boxes)
LogBox.ignoreAllLogs();

Sentry.init({
    // iOS reports to the dedicated `happy-ios` Sentry project; Android (and web)
    // stay on `happy-app`. Both projects live in the same org/region
    // (o4511590534348800, EU / de.sentry.io). iOS source-map + dSYM uploads are
    // repointed to happy-ios in ios/sentry.properties — kept in sync across every
    // `expo prebuild` by the ./plugins/withSentryIosProject config plugin — so iOS
    // events stay symbolicated. Changing the iOS project here without that upload
    // change would leave iOS stack traces unreadable.
    dsn: Platform.select({
        ios: "https://b69a35affc2464d96e9d06709f7e69b0@o4511590534348800.ingest.de.sentry.io/4511598672019536",
        default: "https://584fa43f5472b5bea80fb6b339f7197d@o4511590534348800.ingest.de.sentry.io/4511590550470736",
    }),
    // Adds more context data to events (IP address, cookies, user, etc.)
    // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
    sendDefaultPii: true,
    // Free-plan mode: tracing/profiling OFF. happy-app / happy-android /
    // happy-ios share one org-wide free-plan quota; performance spans + profiles
    // were draining it and triggering 429s that dropped real crashes/errors.
    // Bump these to a small non-zero value once the org is on a paid plan.
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    // Do NOT forward structured/console logs — the free-plan log budget is
    // limited and adds to the quota drain.
    enableLogs: false,
    // Mobile Session Replay. Privacy-first: mask ALL text, images and vectors so
    // no customer / payment / PII data ever leaves the device. To protect the
    // JS thread on low-end Android POS hardware we do NOT record idle sessions
    // in production (rate 0); we only capture a replay around an actual error,
    // which is where reproduction value is highest. Bump replaysSessionSampleRate
    // to 1.0 temporarily when validating that replays arrive in Sentry.
    replaysSessionSampleRate: 0,
    // `onError` replay keeps a continuously-buffered recording so it can flush the
    // seconds BEFORE an error. That buffering runs the native redaction pass
    // (SentryUIRedactBuilder.mapRedactRegion) on the MAIN thread every frame, and
    // on rich/complex screens it blocked the UI long enough for the iOS watchdog
    // to kill the app (Fatal App Hang, Sentry HAPPY-IOS-4). Sampling the buffer
    // means only a fraction of sessions pay that main-thread redaction cost, which
    // removes the hang for the vast majority of users while still capturing
    // error replays. iOS (where the watchdog fired) is sampled most aggressively.
    replaysOnErrorSampleRate: 0,
    // Pass `integrations` as a FUNCTION (not an array) so we can REMOVE a
    // default integration. Passing an array would only *merge* with the
    // defaults. We strip Sentry's `ExpoUpdatesListener`: on `afterInit` it
    // runs `require('expo-updates')`, but this app intentionally ships WITHOUT
    // expo-updates (OTA disabled: `expo.modules.updates.ENABLED=false`, and the
    // package is not installed). Metro bundles that require to a module slot
    // that is absent from the Hermes bundle, so it throws
    // `Requiring unknown module "3329"` and fatally crashes the JS thread on
    // boot. Removing the integration removes the only call site that requires
    // the missing module. See @sentry/react-native expoupdateslistener.
    // Session Replay is OFF on the free plan (replays have their own small cap),
    // so we no longer add mobileReplayIntegration — which also removes the
    // native main-thread redaction pass that caused the iOS App Hang
    // (HAPPY-IOS-4). We STILL strip Sentry's `ExpoUpdatesListener`: on afterInit
    // it `require('expo-updates')`, which this app ships without, throwing
    // "Requiring unknown module" and fatally crashing the JS thread on boot.
    integrations: (defaultIntegrations) => [
        ...defaultIntegrations.filter(
            (integration) => integration && integration.name !== 'ExpoUpdatesListener',
        ),
    ],
    // Keep a small breadcrumb buffer to cap memory.
    maxBreadcrumbs: 30,
    // Drop the benign, retryable Fabric (new-architecture) view-mounting race.
    // "RetryableMountingLayerException: Unable to find viewState for tag N.
    // Surface stopped: false" is thrown by React Native's SurfaceMountingManager
    // when a view tag is referenced after its surface state changed — the class
    // name is literally Retryable and RN self-retries the mount. Pure framework
    // noise with no app-code frames (Sentry HAPPY-APP-J). @sentry/react-native
    // forwards ignoreErrors to the native Android SDK, so these drop the native
    // event too. Kept narrow on purpose: we do NOT match the generic
    // "Exception thrown when executing UIFrameGuarded" wrapper, which could also
    // wrap a future genuinely-actionable mounting crash.
    ignoreErrors: [
        /RetryableMountingLayerException/,
        /Unable to find viewState for tag/,
    ],
    // Drop benign network-cancel noise (AbortController.abort(), fetch/XHR
    // cancellation on unmount/navigation). These are not actionable and only
    // add noise (Sentry HAPPY-APP-C "abort"). We inspect the error type/name,
    // message and the captured exception values so we catch both JS Error
    // objects and the synthetic events Sentry builds for native/promise
    // rejections.
    beforeSend(event, hint) {
        try {
            const original = hint && hint.originalException;
            const name =
                (original && (original.name || original.constructor?.name)) || '';
            const message =
                (original && (original.message || (typeof original === 'string' ? original : ''))) ||
                event?.message ||
                '';
            const exceptionValues = event?.exception?.values || [];
            const exceptionMatch = exceptionValues.some((v) => {
                const type = (v && v.type) || '';
                const value = (v && v.value) || '';
                return (
                    type === 'AbortError' ||
                    /\bAbortError\b/i.test(value) ||
                    /\b(aborted|the operation was aborted|request aborted)\b/i.test(value)
                );
            });

            if (
                name === 'AbortError' ||
                /\bAbortError\b/i.test(String(message)) ||
                /^abort$/i.test(String(message).trim()) ||
                /\b(the operation was aborted|request aborted|aborted)\b/i.test(String(message)) ||
                exceptionMatch
            ) {
                return null; // drop the event
            }
        } catch (_) {
            // Never let the filter itself break error reporting.
        }
        return event;
    },
});

if (!__DEV__) {
    // NOTE: The previous console.* forwarder that stringified every arg
    // and pushed a Sentry breadcrumb per log call has been removed. It
    // blocked the JS thread on every log, allocated a string per breadcrumb,
    // and leaked references to large objects. Sentry already captures
    // unhandled errors via installGlobalHandlers + ErrorBoundary, and
    // babel-plugin-transform-remove-console strips log/info/debug from the
    // production bundle, so breadcrumb forwarding was redundant.

    const initializeUserTracking = async () => {
        try {
            const user = await getUserInfo();
            if (!user) return;
            Sentry.setUser({
                id: user._id,
                email: user.email,
                name: user.name ? `${user.name.first || ''} ${user.name.last || ''}`.trim() : undefined,
            });
        } catch (_) {
            // Never fail boot because of user-tracking setup.
        }
    };

    initializeUserTracking();
}

/**
 * Register the FCM background-message handler. Kept in a function (invoked AFTER
 * registerRootComponent below) instead of as a bare top-level side effect: on
 * iOS `messaging()` touches the native Firebase module during entry-module
 * evaluation, and ANY synchronous throw here (native module not ready, Firebase
 * edge case) would abort index.js before `registerRootComponent` runs — leaving
 * AppRegistry with no 'main' component, so React Native never commits a first
 * frame and iOS stays stuck on the native launch storyboard (the exact
 * "stuck at splash" App Store rejection). The try/catch + post-registration
 * ordering guarantee push setup can never block the app from mounting.
 * @returns {void}
 */
function registerPushBackgroundHandler() {
    if (Platform.OS !== 'android' && Platform.OS !== 'ios') {
        return;
    }

    try {
        setPushBackgroundMessageHandler(async (message) => {
            // Incoming VoIP call (Android killed/background path): a high-priority
            // data-only push with type=incoming_call must ring the native call UI
            // via CallKeep — NOT a passive banner. (iOS uses PushKit, not FCM.)
            // Caller hung up / nobody answered: stop the ringing call.
            if (message?.data?.type === 'call_ended') {
                endIncomingCallFromPush(message.data);
                return;
            }
            if (message?.data?.type === 'incoming_call') {
                // iOS: PushKit already rang CallKit natively with the same uuid.
                if (Platform.OS === 'ios' && isVoipPushKitActive()) {
                    return;
                }
                // The push may be fanned out to every device of the store: ring
                // only for the addressed user (payload without targetUserId = all).
                const target = message.data.targetUserId ? String(message.data.targetUserId) : '';
                if (target) {
                    try {
                        // eslint-disable-next-line global-require
                        const me = await require('./app/helpers/storage').getUserInfo();
                        const myId = me && (me._id || me.id) ? String(me._id || me.id) : '';
                        if (myId && myId !== target) {
                            return;
                        }
                    } catch (_error) {
                        // Unknown local user — fall through and ring.
                    }
                }
                displayIncomingCallFromPush(message.data);
                return;
            }

            // Silent home-screen-widget sync (backend
            // widgetSync.service#notifyWidgetTimerChanged). Data-only by design:
            // it exists purely so the widget repaints within seconds after the
            // admin panel (or another device) started/paused/resumed/stopped the
            // time tracker. It must NEVER present a banner, so it is handled
            // first-class and returns before the local-notification fallback.
            // Lazily required + fully guarded: this runs headless, where an
            // import failure would otherwise kill the whole handler.
            if (message?.data?.type === 'widget.refresh') {
                try {
                    // eslint-disable-next-line global-require
                    const refreshWidgets = require('./app/helpers/widgets/widgetRefresh').default;
                    await refreshWidgets();
                } catch (_error) {
                    // A widget refresh failure must stay invisible to the user.
                }

                return;
            }

            // Pushes that carry an FCM `notification` block are rendered by the OS
            // automatically while backgrounded/killed — re-presenting them would
            // double-notify. Only DATA-ONLY pushes (no notification block) need a
            // local banner built here as a safety net.
            // Chat / meeting / call pushes also repaint the collaboration widgets
            // (TeamChat / Meetings / Calls). Fire-and-forget and fully guarded:
            // the banner below must never wait on, or fail because of, a widget.
            try {
                // eslint-disable-next-line global-require
                const widgetRefresh = require('./app/helpers/widgets/widgetRefresh');
                if (widgetRefresh.isWidgetRelevantPush(message?.data)) {
                    widgetRefresh.default().catch(() => {});
                }
            } catch (_error) {
                // A widget refresh failure must stay invisible to the user.
            }

            const hasNotificationBlock = Boolean(message?.raw?.notification);
            if (!hasNotificationBlock) {
                await presentPushAsLocalNotification(message);
            }
        });
    } catch (error) {
        // Never let push setup abort boot / component registration.
        console.error('[Boot] push background handler registration failed:', error);
    }
}


/**
 * Root wrapper that enables a TRUE "reload from the splash screen" without
 * killing the OS process. Bumping `reloadKey` remounts the whole <App/> tree,
 * which resets `appIsReady`, replays the splash animation, re-runs
 * loadRuntimeState() and re-reads the persisted `userAppMode` — i.e. the exact
 * same boot/routing path as a cold start. Triggered via the global `restartApp`
 * observable (used when switching the active app module).
 *
 * The Redux store + persistor are module-level singletons (see ./app/store), so
 * a remount keeps the user logged in and state intact; only the boot/splash and
 * mode-routing flow re-runs. This works identically on native, Electron and web.
 */
function Root(props) {
    const [reloadKey, setReloadKey] = React.useState(0);

    React.useEffect(() => {
        const handler = () => setReloadKey((key) => key + 1);
        Observables.register('restartApp', handler);
        return () => Observables.remove('restartApp');
    }, []);

    // First metric emissions: one boot count + the JS-init→mount cold-start
    // duration, both tagged by platform so Android vs iOS POS performance can be
    // compared in Sentry. Runs once on the initial mount (not on soft reloads).
    React.useEffect(() => {
        const attributes = { platform: Platform.OS };

        trackMetric('count', 'app.boot', 1, { unit: 'launch', attributes });
        trackMetric('distribution', 'app.js_init_to_mount', Date.now() - jsInitAt, {
            unit: 'millisecond',
            attributes,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <App key={reloadKey} {...props} />;
}

// registerRootComponent calls AppRegistry.registerComponent('main', () => Root);
// It also ensures that whether you load the app in Expo Go or in a native build,
// the environment is set up appropriately.
//
// Wrap the root with Sentry.wrap() so the SDK can attach its touch-event
// breadcrumb tracking, automatic performance instrumentation and a top-level
// error boundary around the entire component tree.
// Web build opened in a phone browser (services.happysolutions.hu) → hand off
// to the installed native app once per tab; the web app still boots below as
// the fallback (no app installed / launch refused).
if (Platform.OS === 'web') {
    // Lazy i18n lookup: the toast renders seconds later, after i18n.js init.
    maybeOpenNativeApp((key, fallback) => require('./i18n').default.t(key, {defaultValue: fallback}));
}

registerRootComponent(Sentry.wrap(Root));

// Register the push background handler ONLY after the root component is
// registered, so no native call it makes can ever prevent the app from
// mounting. Runs on the next microtask on native; a no-op on web.
registerPushBackgroundHandler();

/**
 * Wire native VoIP call handling at module scope so it survives a cold,
 * push-triggered launch (app was killed): CallKeep (CallKit / ConnectionService)
 * for the ringing UI, PushKit for iOS VoIP pushes, and the answer/end actions.
 * When the user answers from the lock screen / native UI, bring the app forward
 * and hand off to the in-app call flow via a global observable + deep navigate.
 * Fully guarded — a missing native module (pre-rebuild) can never abort boot.
 * @returns {void}
 */
function registerVoipCallHandling() {
    if (Platform.OS !== 'android' && Platform.OS !== 'ios') {
        return;
    }
    try {
        setupCallKeep();
        setupVoipPushKit();
        registerCallKeepEvents({
            onAnswer: (info) => {
                bringAppToForeground();
                // Latch at module scope: on a cold start CallProvider is not mounted
                // yet and the observable below would otherwise be lost.
                recordPendingAnswer(info);
                Observables.call('voip:answer', info);
                navigateWhenReady('IncomingCall', { ...info, answered: true });
            },
            onEnd: (info) => {
                clearPendingAnswer();
                Observables.call('voip:end', info);
            },
            onMute: (muted, info) => {
                Observables.call('voip:mute', { muted, ...info });
            },
        });
        // Android call notification (self-managed CallKeep has no system UI):
        // Answer/Decline route into the handlers above; the full-screen intent
        // over the lock screen opens the ringing screen. No-op on iOS.
        startIncomingCallNotificationRouting({
            onShow: (info) => navigateWhenReady('IncomingCall', info),
        });
    } catch (error) {
        console.error('[Boot] VoIP call handling registration failed:', error);
    }
}

registerVoipCallHandling();

// Register the Android home-screen widget headless task. This lets the OS render
// widgets (add/update/resize/click) outside the app UI. Android-only and fully
// guarded so a missing native module can never abort boot.
if (Platform.OS === 'android') {
    try {
        // eslint-disable-next-line global-require
        const { registerWidgetTaskHandler } = require('react-native-android-widget');
        // eslint-disable-next-line global-require
        const { widgetTaskHandler } = require('./app/widgets/widgetTaskHandler');
        registerWidgetTaskHandler(widgetTaskHandler);
    } catch (error) {
        console.error('[Boot] widget task handler registration failed:', error);
    }

    // Configuration screen of the insight widgets (finance / bank / sales /
    // operations / custom). Guarded separately so it can never abort boot.
    try {
        // eslint-disable-next-line global-require
        const { registerWidgetConfigurationScreen } = require('react-native-android-widget');
        // eslint-disable-next-line global-require
        const WidgetConfigurationScreen = require('./app/widgets/WidgetConfigurationScreen').default;
        registerWidgetConfigurationScreen(WidgetConfigurationScreen);
    } catch (error) {
        console.error('[Boot] widget configuration screen registration failed:', error);
    }
}
